import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-agentic-ai',
  imports: [CommonModule, RouterModule],
  standalone: true,
  templateUrl: './agentic-ai.component.html',
  styleUrl: './agentic-ai.component.scss'
})
export class AgenticAiComponent implements OnInit, AfterViewInit {
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  private jsonLdScript: any;
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
      .subscribe((event: any) => {
        if (this.router.url === '/blogs/agentic-ai') {
          this.updateDescription(event.description, event.keywords, event.title);
        }
      });
  }
  injectSchemaMarkup(): void {
    const fullGraphSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/blogs/agentic-ai",
          "url": "https://www.kalpitatechnologies.com/blogs/agentic-ai",
          "name": "A Comprehensive Guide to Agentic AI: Components, Implementation, and Examples",
          "description": "Comprehensive 2025 guide to Agentic AI covering what it is, core components, implementation strategies, and real-world examples.",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com/"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/blogs/agentic-ai#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/tech-blogs/blog", "name": "Blog" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/blogs/agentic-ai", "name": "A Comprehensive Guide to Agentic AI" } }
          ]
        },
        {
          "@type": "BlogPosting",
          "headline": "A Comprehensive Guide to Agentic AI: Components, Implementation, and Examples",
          "articleBody": "Agentic AI is rapidly emerging as a transformative technology that takes artificial intelligence beyond static models into adaptive, goal-oriented systems...",
          "author": { "@type": "Person", "name": "Nagaraj Mantha" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-01-28",
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/agentic-ai/agentic-banner.png"
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is Agentic AI?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Agentic AI refers to AI systems that behave like agents — entities capable of perceiving their environment, making decisions, and taking actions to achieve specific goals."
              }
            }
          ]
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(fullGraphSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  updateDescription(desc: string, keywords: string, title: string) {
    // this.seoService.updateDescription(desc);
    // this.seoService.updateMetaKeywords(keywords);
    // this.seoService.updateTitle(title);
  }

  nav(url: any) {
    this.router.navigateByUrl(url)
  }

  @ViewChild('responsiveImage', { static: false }) image!: ElementRef<HTMLImageElement>;
  @ViewChild('tocImage') tocImage!: ElementRef<HTMLImageElement>;

  originalWidth = 3599;
  originalHeight = 3090;

  tocOriginalWidth = 3840;
  tocOriginalHeight = 1462;

  ngAfterViewInit(): void {
    this.resizeImageMap();
    this.setupTocImageMap();
    this.image.nativeElement.onload = () => this.resizeImageMap();
  }

  setupTocImageMap() {
    if (!this.tocImage?.nativeElement) return;

    const img = this.tocImage.nativeElement;

    const scaleMapAreas = () => {
      const widthRatio = img.clientWidth / this.tocOriginalWidth;
      const heightRatio = img.clientHeight / this.tocOriginalHeight;

      const map = document.querySelector('map[name="toc-image-map"]');
      if (!map) return;

      map.querySelectorAll('area').forEach((area) => {
        const originalCoords = area.getAttribute('coords');
        if (!originalCoords) return;

        const coords = originalCoords.split(',').map(Number);
        const scaledCoords = coords.map((coord, index) =>
          index % 2 === 0 ? Math.round(coord * widthRatio) : Math.round(coord * heightRatio)
        );
        area.setAttribute('coords', scaledCoords.join(','));
      });
    };

    img.onload = scaleMapAreas;

    if (img.complete) {
      scaleMapAreas();
    }
  }

  @HostListener('window:resize')
  resizeImageMap() {
    if (!this.image?.nativeElement) return;
    const img = this.image.nativeElement;
    const widthRatio = img.clientWidth / this.originalWidth;
    const heightRatio = img.clientHeight / this.originalHeight;

    const map = document.querySelector('map[name="image-map"]');

    if (!map) return;
    map.querySelectorAll('area').forEach((area) => {
      let coords = area.getAttribute('data-original-coords');
      if (!coords) {
        coords = area.getAttribute('coords');
        area.setAttribute('data-original-coords', coords || '');
      }
      if (!coords) return;
      const originalCoords = coords.split(',').map(Number);
      const scaledCoords = originalCoords.map((coord, index) =>
        index % 2 === 0 ? Math.round(coord * widthRatio) : Math.round(coord * heightRatio)
      );
      area.setAttribute('coords', scaledCoords.join(','));
    });
  }

  scrollToSection(sectionId: string): void {
    const targetElement = document.getElementById(sectionId);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const offset = 5 * 16;
      const scrollPosition = rect.top + window.pageYOffset - offset;
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }
  }
}
